import { Button } from '../components/Button.jsx';
import { Card } from '../components/Card.jsx';
import { YearStrip } from '../components/YearStrip.jsx';
import { useFocusOnMount } from '../components/useFocusOnMount.js';
import { acknowledgeSubsidy } from '../state/actions.js';
import {
  lastYearResult,
  currentDemand,
  currentPeakDemand,
  isAdvancedMode
} from '../state/selectors.js';
import { formatTwh, formatGw, formatMoney } from '../components/format.js';
import { configForState } from '../state/modeConfig.js';
import { RESERVE_MARGIN } from '../data/advancedCapacity.js';

export function SubsidyRevealScreen({ state, dispatch }) {
  const mainRef = useFocusOnMount();
  const last = lastYearResult(state);
  const justRemoved = last?.subsidyReformed === true;
  const isAdvanced = isAdvancedMode(state);
  const demand = currentDemand(state);
  const peakDemand = isAdvanced ? currentPeakDemand(state) : null;
  const config = configForState(state);
  const year1Slots = config.slotsNewPerYear[1] ?? 0;

  // The derate is invisible unless it is stated: a player who does not
  // know the coal runs at 75% cannot weigh keeping it against
  // replacing it. Derived from the plant data rather than written out,
  // so recalibrating a derate factor cannot leave this prose stale.
  const legacyPlants = config.startingPortfolio.map(
    (id) => config.plantsById[id]
  );
  const legacyOutput = legacyPlants.reduce((s, p) => s + p.output, 0);
  const legacyFirm = legacyPlants.reduce(
    (s, p) => s + p.capacityUnits * p.firmCapacityCredit,
    0
  );
  const legacyOpex = legacyPlants.reduce((s, p) => s + p.opex, 0);
  const requiredFirm = peakDemand === null ? 0 : peakDemand * RESERVE_MARGIN;

  return (
    <main ref={mainRef} id="main" tabIndex={-1} className="screen screen-subsidy-reveal">
      <YearStrip
        year={state.year}
        subsidyActive={state.subsidyActive}
        portfolioCount={state.portfolio.length}
        demandLabel={isAdvanced ? formatTwh(demand) : undefined}
        action={state.year === 1 ? (
          <Button
            onClick={() => dispatch(acknowledgeSubsidy())}
            changesScreen
            testId="subsidy-continue-banner"
          >
            Continue to build phase
          </Button>
        ) : null}
      />
      <Card
        title={
          state.year === 1 && isAdvanced
            ? 'Advanced Mode: 3 Key Additions'
            : `Year ${state.year} begins`
        }
        titleId="subsidy-reveal-heading"
      >
        {state.year === 1 ? (
          <div className="year-intro-layout">
            <div className="year-intro-copy">
              {isAdvanced ? (
                <>
                  <p className="card-body year-intro-note year-intro-note-capacity">
                    <strong>Firm Capacity:</strong> Meet peak demand of{' '}
                    {formatGw(peakDemand)} plus a 10% reserve. Repeated
                    variable resources earn declining firm credit, while a
                    wider mix of resource families can raise it.
                  </p>
                  <p className="card-body year-intro-note year-intro-note-storage">
                    <strong>Storage Technologies:</strong> Storage adds firm
                    capacity only when expected surplus can charge it. Grid
                    Batteries reset each year, while long-duration storage
                    carries charge forward.
                  </p>
                  {legacyPlants.length > 0 ? (
                    <p className="card-body year-intro-note year-intro-note-residual">
                      <strong>Residual Capacity:</strong> You inherit{' '}
                      {legacyPlants.map((p, i) => (
                        <span key={p.id}>
                          {i > 0 ? ' and ' : ''}
                          a {p.name} at{' '}
                          {Math.round(p.legacyDerateFactor * 100)}% of nameplate
                        </span>
                      ))}
                      . Together they supply {formatTwh(legacyOutput)} and{' '}
                      {formatGw(legacyFirm)} of the required{' '}
                      {formatGw(requiredFirm)}, cost {formatMoney(legacyOpex)}
                      {' '}each year, and occupy {legacyPlants.length} of your{' '}
                      {year1Slots} connection slots. Retiring one frees its
                      slot in the same build phase.
                    </p>
                  ) : null}
                </>
              ) : (
                <>
                  <p className="card-body year-intro-note year-intro-note-goal">
                    You are an energy planner. Build a portfolio of power
                    plants across 3 years that meets rising electricity demand
                    at the lowest possible total cost.
                  </p>
                  <p className="card-body year-intro-note year-intro-note-variable">
                    Reliable plants, including fossil fuel plants, produce a
                    fixed amount of electricity. Variable renewable plants
                    produce within the range shown, so their final output is
                    uncertain until the year is resolved.
                  </p>
                  <p className="card-body year-intro-note year-intro-note-shortfall">
                    Shortfall incurs a strict penalty, but surplus output is
                    wasted investment. In Year 1, pick and build up to{' '}
                    {year1Slots} power plants to meet demand of {demand} TWh.
                  </p>
                </>
              )}
            </div>
            <img
              className="year-intro-image"
              src="/energy_planner.webp"
              alt="Energy planner reviewing a power system"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>
        ) : state.subsidyActive ? (
          <>
            <p className="card-body">
              The fossil fuel subsidy is <strong>active</strong>. Fossil
              Thermal opex is halved this year.
            </p>
            <p className="card-body">
              The Subsidy Reform policy signal is still possible;
              if it materialises the subsidy ends for the rest of the game.
            </p>
          </>
        ) : justRemoved ? (
          <>
            <p className="card-body">
              The fossil fuel subsidy was repealed last year by the
              Subsidy Reform policy signal. Fossil Thermal opex now pays the
              full rate.
            </p>
            <p className="card-body">
              Plan accordingly. The cheap fossil bargain is gone.
            </p>
          </>
        ) : (
          <p className="card-body">
            The fossil fuel subsidy is no longer in effect. Fossil
            Thermal opex pays the full rate.
          </p>
        )}
        <div className="screen-actions screen-actions-centre">
          <Button
            onClick={() => dispatch(acknowledgeSubsidy())}
            changesScreen
            testId="subsidy-continue"
          >
            Continue to build phase
          </Button>
        </div>
      </Card>
    </main>
  );
}
